import { useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Download, Printer } from 'lucide-react';
import { DEFAULT_FORM_LOCALE, DOC_LOCALES, type DocLocale } from '@eombor/contracts';
import { Button, Dialog, Skeleton, cn } from '@eombor/ui';
import { downloadBlob } from '../../../shared/api/download.js';
import { printBlob } from '../../../shared/api/print.js';
import { t } from '../../../shared/i18n/index.js';
import { Select } from '../../../shared/ui/Select.js';
import { requisitionApi } from '../../requisition/api/requisition.api.js';
import { accountingApi } from '../api/accounting.api.js';

/**
 * Blank manbasi — OCHILGAN so'rovnoma yoki hali ochilmagan talabnoma.
 *
 * Ikkinchisi qoralama: blank ustida "QORALAMA" turadi va raqam o'rni bo'sh
 * qoladi. Skladchi qog'ozni aynan shu holatda ko'zdan kechiradi.
 */
export type RequirementSource = { survey: string } | { requisition: string };

export interface RequirementDialogProps {
  open: boolean;
  source: RequirementSource;
  /** Hujjat raqami — sarlavhada va fayl nomida. */
  docNo: string;
  onClose: () => void;
  /**
   * Ko'rib bo'lgach bajariladigan amal — "So'rovnoma ochish".
   *
   * Berilsa, oyna qaror ekraniga aylanadi: qog'oz ko'rildi, endi hujjat
   * ochiladi. Berilmasa u shunchaki ko'rinish bo'lib qoladi.
   */
  confirm?: { label: string; pending?: boolean; onConfirm: () => void };
}

/**
 * TREBOVANIYA — ФМУ №25 blankining OLDINDAN KO'RINISHI.
 *
 * Ko'rinish PDF ning O'ZI, uning brauzerdagi qayta chizilgan nusxasi emas:
 * buxgalter ko'rgan narsa printerdan chiqadigan narsa bilan bir xil
 * bo'lishi kerak. HTML bilan "shunga o'xshash" jadval chizish esa bir kun
 * kelib qog'ozdan farq qilib qolardi va buni faqat bosgandan keyin bilib
 * bo'lardi.
 *
 * TIL TANLAGICHI: ilova o'zbekcha ishlayotgan bo'lsa ham, chop etishga
 * ketadigan qog'oz buxgalterning dastasiga tushadi va u ruscha. Shuning
 * uchun tanlov RUSCHADAN boshlanadi, lekin qulflangan emas.
 */
export const RequirementDialog = ({
  open,
  source,
  docNo,
  onClose,
  confirm,
}: RequirementDialogProps) => {
  const [locale, setLocale] = useState<DocLocale>(DEFAULT_FORM_LOCALE);

  const draft = !('survey' in source);
  const key = 'survey' in source ? source.survey : source.requisition;

  const blank = useQuery({
    queryKey: ['accounting', 'requirement', draft ? 'draft' : 'survey', key, locale],
    queryFn: () =>
      'survey' in source
        ? accountingApi.requirementPdf(source.survey, locale)
        : requisitionApi.requirementPdf(source.requisition, locale),
    enabled: open,
    /* Ochilgan hujjat O'ZGARMAYDI — uni qayta so'rash bekorga trafik.
       Qoralama esa talabnoma bilan birga o'zgaradi: skladchi miqdorni
       to'g'rilab, blankni qaytadan ochsa yangi raqamlarni ko'rishi kerak. */
    staleTime: draft ? 0 : 5 * 60_000,
    retry: false,
  });

  /*
   * `blob:` manzili — javobda `Content-Disposition: attachment` turadi va
   * to'g'ridan-to'g'ri ochilsa brauzer PDF ni ko'rsatmay, saqlab qo'yardi.
   * Manzil oyna yopilganda BO'SHATILADI: aks holda har ochilishda
   * brauzer xotirasida bitta PDF qolib ketardi.
   */
  const [url, setUrl] = useState<string | null>(null);
  useEffect(() => {
    const file = blank.data;
    if (file === undefined) return;

    const next = URL.createObjectURL(file);
    setUrl(next);
    return () => {
      URL.revokeObjectURL(next);
      setUrl(null);
    };
  }, [blank.data]);

  const fileName = draft ? `${docNo}-trebovaniya-qoralama.pdf` : `${docNo}-trebovaniya.pdf`;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      title={t(draft ? 'accounting.requirementDraftTitle' : 'accounting.requirementTitle', {
        docNo,
      })}
      size="lg"
      className="max-w-5xl"
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            {t('accounting.close')}
          </Button>
          <Button
            variant="secondary"
            disabled={blank.data === undefined}
            onClick={() => {
              if (blank.data !== undefined) downloadBlob(blank.data, fileName);
            }}
          >
            <Download size={14} aria-hidden />
            {t('accounting.downloadPdf')}
          </Button>
          {/* Qoralamada ASOSIY amal — hujjatni OCHISH: qog'oz ko'rildi,
              endi u raqam oladi. Ochilgan hujjatda esa BOSISH: u
              imzolanadi va buxgalterga qo'lda topshiriladi. */}
          <Button
            variant={confirm === undefined ? 'primary' : 'secondary'}
            disabled={blank.data === undefined}
            onClick={() => {
              if (blank.data !== undefined) printBlob(blank.data);
            }}
          >
            <Printer size={14} aria-hidden />
            {t('accounting.requirementPrint')}
          </Button>
          {confirm === undefined ? null : (
            <Button loading={confirm.pending ?? false} onClick={confirm.onConfirm}>
              {confirm.label}
            </Button>
          )}
        </>
      }
    >
      <div className="flex flex-col gap-3">
        <div className="flex flex-wrap items-end justify-between gap-3">
          <p className="max-w-prose text-sm text-text-muted">
            {t(draft ? 'accounting.requirementDraftHint' : 'accounting.requirementHint')}
          </p>
          <Select
            label={t('accounting.requirementLocale')}
            className="w-[11rem]"
            value={locale}
            options={DOC_LOCALES.map((code) => ({
              value: code,
              label: t(`accounting.requirementLocale.${code}`),
            }))}
            onChange={(event) => setLocale(event.target.value as DocLocale)}
          />
        </div>

        {blank.isError ? (
          <p role="alert" className="text-sm text-danger">
            {t('accounting.requirementFailed')}
          </p>
        ) : url === null ? (
          <Skeleton className="h-[26rem] w-full" />
        ) : (
          /*
           * Balandlik QAT'IY: `iframe` o'z mazmuniga qarab o'smaydi va
           * balandliksiz u nol piksel bo'lib qolardi. Albom yo'nalishidagi
           * A4 nisbati taxminan 1,41 — quti shunga yaqin tutiladi.
           */
          <iframe
            src={url}
            title={t(
              draft ? 'accounting.requirementDraftTitle' : 'accounting.requirementTitle',
              { docNo },
            )}
            className={cn(
              'h-[26rem] w-full rounded-[var(--radius-sm)] border border-border',
              'bg-surface-2',
            )}
          />
        )}
      </div>
    </Dialog>
  );
};
